import React, { useCallback, useMemo } from 'react';
import { CustomSelect } from 'apps/sigma2-ui/src/components/ui/CustomSelect';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DefaultOverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { OverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { OverallDisplayModeFilterProps } from 'libs/end-to-end/src/view-configurations';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';

export const OverallDisplayModeFilter = (
  props: OverallDisplayModeFilterProps,
) => {
  const { setViewConfiguration, viewConfiguration } = props;
  const language = useLanguage();

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const metricInfo = useMemo(() => {
    if (!viewConfiguration.selectedMetric) {
      return undefined;
    }

    return getMetricAndFocusAreaFromColumnName({
      columnName: viewConfiguration.selectedMetric,
    });
  }, [viewConfiguration.selectedMetric, getMetricAndFocusAreaFromColumnName]);

  const overallDisplayOptions = useMemo(() => {
    const isMetricBoolean = metricInfo?.metric.isMetricBoolean ?? true;
    const canDisableCount =
      !isMetricBoolean && viewConfiguration.overallDisplayMode === 'average';

    return [
      {
        value: 'average' as const,
        label: language.overallDisplayModeAverageLabel(),
      },
      {
        value: 'count' as const,
        label: language.overallDisplayModeCountLabel(),
        disabled: canDisableCount,
      },
    ];
  }, [language, metricInfo, viewConfiguration.overallDisplayMode]);

  const selectedOverallDisplay = useMemo(() => {
    return viewConfiguration?.overallDisplayMode ?? DefaultOverallDisplayMode;
  }, [viewConfiguration]);

  const overallDisplayChangeCb = useCallback(
    (value: OverallDisplayMode) => {
      if (setViewConfiguration && viewConfiguration) {
        setViewConfiguration({
          ...viewConfiguration,
          overallDisplayMode: value,
        });
      }
    },
    [setViewConfiguration, viewConfiguration],
  );

  return (
    <div className="flex items-center gap-2">
      <CustomSelect
        selectedValue={selectedOverallDisplay}
        options={overallDisplayOptions}
        onChange={overallDisplayChangeCb}
      />
    </div>
  );
};
